import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

const BoldText = ({ text }) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="text-[#FDF8EE] font-semibold">{part.slice(2, -2)}</strong>
      : part
  );

const refillDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const nudgeText = (u) => {
  if (u.is_trial) {
    if (u.remaining === 1) return { text: "This is your last free question", link: "Upgrade to Signal for 50 questions a month" };
    return { text: `${u.remaining} of ${u.queries_limit} free questions left`, link: u.remaining <= 2 ? "Upgrade" : null };
  }
  if (u.remaining === 0) return null;
  const refill = ` · refills ${refillDate(u.resets_at)}`;
  const counts = `${u.queries_used} of ${u.queries_limit}`;
  if (u.warning) {
    return u.pooled
      ? { text: `Your team has used ${counts} pooled questions this month${refill}`, link: null }
      : { text: `You've used ${counts} questions this month${refill}`, link: "Upgrade to Synthesis" };
  }
  return { text: u.pooled ? `Team: ${counts} used${refill}` : `${counts} used${refill}`, link: null };
};

const UsageNudge = ({ usage, onUpgrade }) => {
  const n = usage && nudgeText(usage);
  if (!n) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 mb-2 border border-[rgba(232,160,48,0.18)] bg-[rgba(232,160,48,0.05)] text-[11px] text-[#E6D9BF]" data-testid="ask-ryan-usage-nudge">
      <span className="text-[#E8A030]">◈</span>
      <span data-testid="ask-ryan-nudge-text">{n.text}</span>
      {n.link && (
        <>
          <span className="text-[#8C7A5A]">·</span>
          <button type="button" onClick={() => onUpgrade("ask_ryan_nudge")} className="text-[#E8A030] hover:underline font-medium" data-testid="ask-ryan-nudge-upgrade">{n.link}</button>
        </>
      )}
    </div>
  );
};

const GATES = {
  trial: () => ({ title: "You've used your 5 free questions", body: "Upgrade to Signal for 50 questions a month, with answers and citations from everything you've saved.", cta: "Upgrade to Signal - $15/mo" }),
  signal: (u) => ({ title: `You've used all ${u.queries_limit} questions this month`, body: `Your questions refill on ${refillDate(u.resets_at)}. Upgrade to Synthesis for 150 questions per user per month, pooled across your team.`, cta: "Upgrade to Synthesis" }),
  pooled: (u) => ({ title: "Your team is out of questions", body: `Your team has used all ${u.queries_limit} pooled Ask Ryan questions this month. They refill on ${refillDate(u.resets_at)}.`, cta: null }),
};

const LimitGate = ({ usage, onUpgrade, compact }) => {
  const kind = usage.kind;
  const g = GATES[kind](usage);
  return (
  <div className={`flex flex-col items-center justify-center text-center ${compact ? "shrink-0 border-t border-[rgba(232,160,48,0.1)] p-5" : "flex-1 p-8"}`} data-testid="ask-ryan-trial-gate">
    {!compact && (
      <div className="w-16 h-16 bg-[rgba(232,160,48,0.08)] border border-[rgba(232,160,48,0.15)] flex items-center justify-center mb-6">
        <span className="text-[#E8A030] text-2xl font-serif font-bold">R</span>
      </div>
    )}
    <h3 className="font-serif text-lg font-bold text-[#FDF8EE] mb-2" data-testid="ask-ryan-gate-title">{g.title}</h3>
    <p className="text-sm text-[#E6D9BF] max-w-xs mb-5 leading-relaxed" data-testid="ask-ryan-trial-exhausted">{g.body}</p>
    {g.cta && (
      <button onClick={() => onUpgrade(`ask_ryan_gate_${kind}`)} className="btn-primary" data-testid="ask-ryan-upgrade-btn">{g.cta}</button>
    )}
  </div>
  );
};

const AskRyanPanel = ({ isOpen, onClose, subscription, onUpgrade }) => {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const limitReached = usage?.remaining === 0;

  const handleUpgrade = (source) => {
    axios.post(`${API}/events/track`, { event: "upgrade_click", metadata: { source } }).catch(() => {});
    onUpgrade();
  };

  const fetchUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ask-ryan/usage`);
      setUsage(res.data);
    } catch (e) {
      console.error("Usage fetch error:", e);
    }
  }, []);

  const fetchPrompts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ask-ryan/prompts`);
      setPrompts(res.data.prompts || []);
    } catch (e) {
      console.error("Prompts fetch error:", e);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ask-ryan/sessions`);
      setSessions(res.data || []);
    } catch (e) {
      console.error("Sessions fetch error:", e);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchUsage();
      fetchPrompts();
      fetchSessions();
    }
  }, [isOpen, fetchUsage, fetchPrompts, fetchSessions]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const q = question.trim();
    if (!q || loading) return;

    setMessages((prev) => [...prev, { type: "user", text: q }]);
    setQuestion("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ask-ryan/query`, {
        question: q,
        session_id: sessionId,
      });
      const data = res.data;
      setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          text: data.answer,
          citations: data.citations,
          followUps: data.follow_up_questions,
          corpusSize: data.corpus_size,
        },
      ]);
      fetchUsage();
    } catch (err) {
      const detail = err.response?.data?.detail || "Something went wrong";
      if (err.response?.status === 403) {
        setMessages((prev) => [
          ...prev,
          { type: "error", text: detail, upgradeRequired: true },
        ]);
      } else if (err.response?.status === 429) {
        setMessages((prev) => [
          ...prev,
          { type: "error", text: detail, limitReached: true },
        ]);
      } else {
        setMessages((prev) => [...prev, { type: "error", text: detail }]);
        toast.error("Ask Ryan failed");
      }
      fetchUsage();
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = (q) => {
    setQuestion(q);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setShowHistory(false);
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/ask-ryan/session/${sid}`);
      const session = res.data;
      setSessionId(sid);
      const msgs = [];
      for (const ex of session.exchanges || []) {
        msgs.push({ type: "user", text: ex.question });
        msgs.push({
          type: "assistant",
          text: ex.answer,
          citations: [],
          followUps: [],
          corpusSize: ex.corpus_size,
        });
      }
      setMessages(msgs);
      setShowHistory(false);
    } catch (e) {
      toast.error("Failed to load session");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="ask-ryan-panel">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-[520px] bg-[#0A0600] border-l border-[rgba(232,160,48,0.15)] flex flex-col animate-slide-in-right"
        style={{ animation: "slideInRight 0.25s ease-out" }}
      >
        {/* Header */}
        <div className="h-[60px] border-b border-[rgba(232,160,48,0.1)] flex items-center justify-between px-5 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[rgba(232,160,48,0.1)] border border-[rgba(232,160,48,0.2)] flex items-center justify-center">
              <span className="text-[#E8A030] text-sm font-serif font-bold">R</span>
            </div>
            <div>
              <h2 className="font-serif font-bold text-[#FDF8EE] text-sm" data-testid="ask-ryan-title">
                Ask Ryan
              </h2>
              <p className="font-mono text-[8px] text-[#A8987A] uppercase tracking-[1px]">
                Knowledge Synthesis
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {usage && (
              <span
                className="font-mono text-[9px] text-[#D9C8A6] bg-[rgba(232,160,48,0.05)] px-2 py-1 border border-[rgba(232,160,48,0.1)]"
                data-testid="ask-ryan-usage"
              >
                {usage.is_trial ? `${usage.remaining}/${usage.queries_limit} free left` : `${usage.remaining}/${usage.queries_limit} left${usage.pooled ? " · pooled" : ""}`}
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.1)] transition-colors"
              data-testid="ask-ryan-close"
            >
              x
            </button>
          </div>
        </div>

        {/* Content */}
        {limitReached && messages.length === 0 ? (
          <LimitGate usage={usage} onUpgrade={handleUpgrade} />
        ) : (
          <>
            {/* Actions bar */}
            <div className="flex items-center justify-between px-5 py-2 border-b border-[rgba(232,160,48,0.06)]">
              <div className="flex gap-2">
                <button
                  onClick={startNewSession}
                  className="font-mono text-[9px] text-[#8C7A5A] hover:text-[#E8A030] uppercase tracking-[1px] transition-colors px-2 py-1 hover:bg-[rgba(232,160,48,0.05)]"
                  data-testid="ask-ryan-new-session"
                >
                  + New
                </button>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={`font-mono text-[9px] uppercase tracking-[1px] transition-colors px-2 py-1 ${
                    showHistory
                      ? "text-[#E8A030] bg-[rgba(232,160,48,0.1)]"
                      : "text-[#8C7A5A] hover:text-[#E8A030] hover:bg-[rgba(232,160,48,0.05)]"
                  }`}
                  data-testid="ask-ryan-history-toggle"
                >
                  History
                </button>
              </div>
            </div>

            {showHistory ? (
              /* Session history */
              <ScrollArea className="flex-1">
                <div className="p-4 space-y-2">
                  {sessions.length === 0 ? (
                    <p className="text-sm text-[#A8987A] text-center py-8">
                      No previous sessions
                    </p>
                  ) : (
                    sessions.map((s) => (
                      <button
                        key={s.session_id}
                        onClick={() => loadSession(s.session_id)}
                        className="w-full text-left p-3 border border-[rgba(232,160,48,0.08)] hover:border-[rgba(232,160,48,0.2)] hover:bg-[rgba(232,160,48,0.03)] transition-all group"
                        data-testid={`session-${s.session_id}`}
                      >
                        <p className="text-sm text-[#F5ECD0] truncate group-hover:text-[#FDF8EE]">
                          {s.preview || "Empty session"}
                        </p>
                        <p className="font-mono text-[9px] text-[#A8987A] mt-1">
                          {s.updated_at
                            ? new Date(s.updated_at).toLocaleDateString(
                                "en-US",
                                { month: "short", day: "numeric" }
                              )
                            : ""}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            ) : (
              /* Chat area */
              <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ask-ryan-messages">
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <div className="w-12 h-12 bg-[rgba(232,160,48,0.06)] border border-[rgba(232,160,48,0.12)] flex items-center justify-center mb-4">
                      <span className="text-[#E8A030] font-serif font-bold">R</span>
                    </div>
                    <p className="text-sm text-[#8C7A5A] mb-6 max-w-xs">
                      Ask me anything about your saved bookmarks. I'll
                      synthesize answers from your knowledge base.
                    </p>
                    {prompts.length > 0 && (
                      <div className="space-y-2 w-full max-w-xs">
                        {prompts.slice(0, 4).map((p, i) => (
                          <button
                            key={i}
                            onClick={() => handleFollowUp(p)}
                            className="w-full text-left p-3 border border-[rgba(232,160,48,0.08)] hover:border-[rgba(232,160,48,0.2)] text-[11px] text-[#8C7A5A] hover:text-[#F5ECD0] transition-all hover:bg-[rgba(232,160,48,0.03)]"
                            data-testid={`prompt-suggestion-${i}`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i}>
                    {msg.type === "user" && (
                      <div className="flex justify-end mb-3">
                        <div
                          className="max-w-[85%] bg-[rgba(232,160,48,0.08)] border border-[rgba(232,160,48,0.15)] p-3"
                          data-testid={`user-message-${i}`}
                        >
                          <p className="text-sm text-[#FDF8EE]">{msg.text}</p>
                        </div>
                      </div>
                    )}

                    {msg.type === "assistant" && (
                      <div className="mb-4" data-testid={`assistant-message-${i}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 bg-[rgba(232,160,48,0.1)] flex items-center justify-center">
                            <span className="text-[#E8A030] text-[10px] font-serif font-bold">
                              R
                            </span>
                          </div>
                          <span className="font-mono text-[8px] text-[#A8987A] uppercase tracking-[1px]">
                            Ryan
                          </span>
                          {msg.corpusSize > 0 && (
                            <span className="font-mono text-[8px] text-[#A8987A]">
                              ({msg.corpusSize} bookmarks indexed)
                            </span>
                          )}
                        </div>
                        <div className="pl-7">
                          <div className="text-[13px] text-[#F5ECD0] leading-[1.7] whitespace-pre-wrap">
                            <BoldText text={msg.text} />
                          </div>

                          {/* Citations */}
                          {msg.citations && msg.citations.length > 0 && (
                            <div className="mt-3 space-y-1.5">
                              <span className="font-mono text-[8px] text-[#A8987A] uppercase tracking-[1px]">
                                Sources
                              </span>
                              {msg.citations.map((c, ci) => (
                                <div
                                  key={ci}
                                  className="p-2 border-l-2 border-[#C8600A] bg-[rgba(200,96,10,0.06)]"
                                  data-testid={`citation-${ci}`}
                                >
                                  <p className="text-[10px] text-[#E8A030] font-mono">
                                    {c.collection_name}
                                  </p>
                                  <p className="text-[11px] text-[#8C7A5A] line-clamp-2 mt-0.5">
                                    {c.content_preview}
                                  </p>
                                  {c.source_url && (
                                    <a
                                      href={c.source_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[9px] text-[#E8A030] hover:underline font-mono mt-1 inline-block"
                                    >
                                      View source
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Follow-ups */}
                          {msg.followUps && msg.followUps.length > 0 && (
                            <div className="mt-3 space-y-1.5">
                              <span className="font-mono text-[8px] text-[#A8987A] uppercase tracking-[1px]">
                                Follow up
                              </span>
                              {msg.followUps.map((f, fi) => (
                                <button
                                  key={fi}
                                  onClick={() => handleFollowUp(f)}
                                  className="block w-full text-left p-2 border border-[rgba(232,160,48,0.08)] hover:border-[rgba(232,160,48,0.2)] text-[11px] text-[#8C7A5A] hover:text-[#F5ECD0] transition-all hover:bg-[rgba(232,160,48,0.03)]"
                                  data-testid={`follow-up-${fi}`}
                                >
                                  {f}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {msg.type === "error" && (
                      <div
                        className="mb-3 p-3 border border-[rgba(200,96,10,0.3)] bg-[rgba(200,96,10,0.08)]"
                        data-testid={`error-message-${i}`}
                      >
                        <p className="text-sm text-[#C8600A]">{msg.text}</p>
                        {(msg.upgradeRequired || msg.limitReached) && (
                          <button
                            onClick={() => handleUpgrade("ask_ryan_error")}
                            className="mt-2 font-mono text-[10px] text-[#E8A030] hover:underline uppercase tracking-[1px]"
                          >
                            Upgrade Plan
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="flex items-center gap-3 mb-3" data-testid="ask-ryan-loading">
                    <div className="w-5 h-5 bg-[rgba(232,160,48,0.1)] flex items-center justify-center">
                      <span className="text-[#E8A030] text-[10px] font-serif font-bold">
                        R
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <span
                        className="w-1.5 h-1.5 bg-[#E8A030] animate-pulse"
                        style={{ animationDelay: "0ms" }}
                      />
                      <span
                        className="w-1.5 h-1.5 bg-[#E8A030] animate-pulse"
                        style={{ animationDelay: "150ms" }}
                      />
                      <span
                        className="w-1.5 h-1.5 bg-[#E8A030] animate-pulse"
                        style={{ animationDelay: "300ms" }}
                      />
                    </div>
                    <span className="font-mono text-[9px] text-[#A8987A]">
                      Synthesizing...
                    </span>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}

            {/* Input */}
            {!showHistory && limitReached && <LimitGate usage={usage} onUpgrade={handleUpgrade} compact />}
            {!showHistory && !limitReached && (
              <form
                onSubmit={handleSubmit}
                className="shrink-0 border-t border-[rgba(232,160,48,0.1)] p-4"
              >
                <UsageNudge usage={usage} onUpgrade={handleUpgrade} />
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask about your bookmarks..."
                    className="input-dark flex-1"
                    disabled={loading}
                    data-testid="ask-ryan-input"
                  />
                  <button
                    type="submit"
                    disabled={!question.trim() || loading}
                    className="btn-primary px-4 disabled:opacity-30"
                    data-testid="ask-ryan-submit"
                  >
                    Ask
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AskRyanPanel;
